import { HEAP_APP_ID } from '@utils/env';
import { useEffect } from 'react';
import { Platform } from 'react-native';

/*
This file is a translation of the heap javascript snippet to a react hook.
The original javascript snippet can be found at:
https://developers.heap.io/docs/web

The snippet uses some weird syntax where the heap object starts as an array of
methods but then turns into an object once the library is loaded from their CDN.
This causes the typescript types to be a bit weird.
 */
export function useHeapInitialization(): void {
  useEffect(() => {
    if (Platform.OS === 'web') {
      const appId = HEAP_APP_ID;
      if (appId) {
        const extendedWindow = window as unknown as ExtendedWindow;

        // Initialize heap as an array to queue method calls
        if (!Array.isArray(extendedWindow.heap)) {
          extendedWindow.heap = [];
        }

        // Define the heap.load function
        // ignore typescript error for heap.load
        // @ts-expect-error -- heap requires dynamically created heap methods
        extendedWindow.heap.load = (
          heapAppId: string,
          config?: Record<string, unknown>,
        ): void => {
          const heapQueue = extendedWindow.heap as HeapQueue;

          // ignore typescript error for heap.load
          // @ts-expect-error -- heap requires dynamically created heap methods
          extendedWindow.heap.appid = heapAppId;
          // @ts-expect-error -- heap requires dynamically created heap methods
          extendedWindow.heap.config = config || {};

          // Define the methods that queue calls until Heap is loaded
          const methods: Array<keyof HeapLoaded> = [
            'addEventProperties',
            'addUserProperties',
            'clearEventProperties',
            'identify',
            'resetIdentity',
            'removeEventProperty',
            'setEventProperties',
            'track',
            'unsetEventProperty',
          ];

          if (!Array.isArray(extendedWindow.heap)) {
            for (const method of methods) {
              // @ts-expect-error -- heap required dynamically create heap method
              extendedWindow.heap[method] = ((...args: unknown[]) => {
                heapQueue.push([method, ...args] as HeapMethodCall);
              }) as HeapLoaded[typeof method];
            }
          }

          // Create script element to load Heap library
          const script = document.createElement('script');
          script.type = 'text/javascript';
          script.async = true;
          script.src = `https://cdn.heapanalytics.com/js/heap-${heapAppId}.js`;

          // Insert script into the DOM
          const firstScript = document.getElementsByTagName('script')[0];
          const parentNode = firstScript.parentNode || document.head;

          if (parentNode) {
            parentNode.insertBefore(script, firstScript);
          } else {
            document.head.appendChild(script);
          }
        };

        // Load Heap with your app ID
        if (isHeapLoaded(extendedWindow.heap)) {
          extendedWindow.heap.load(appId);
        }
      } else {
        console.warn('Heap appId not found for web environment');
      }
    }
  }, []);
}

function isHeapLoaded(heap: HeapInstance): heap is HeapLoaded {
  return typeof heap !== 'undefined' && 'load' in heap;
}

type HeapMethodName =
  | 'addEventProperties'
  | 'addUserProperties'
  | 'clearEventProperties'
  | 'identify'
  | 'resetIdentity'
  | 'removeEventProperty'
  | 'setEventProperties'
  | 'track'
  | 'unsetEventProperty';

type HeapMethodArgs = {
  addEventProperties: [properties: Record<string, unknown>];
  addUserProperties: [properties: Record<string, unknown>];
  clearEventProperties: void[]; // Changed from [] to void[]
  identify: [identity: string];
  resetIdentity: void[]; // Changed from [] to void[]
  removeEventProperty: [property: string];
  setEventProperties: [properties: Record<string, unknown>];
  track: [event: string, properties?: Record<string, unknown>];
  unsetEventProperty: [property: string];
};

type HeapMethodCall = {
  [K in HeapMethodName]: [K, ...HeapMethodArgs[K]];
}[HeapMethodName];

type HeapQueue = HeapMethodCall[];

type HeapLoaded = {
  appid?: string;
  config?: Record<string, unknown>;
  load: (appId: string, config?: Record<string, unknown>) => void;
  addEventProperties: (properties: Record<string, unknown>) => void;
  addUserProperties: (properties: Record<string, unknown>) => void;
  clearEventProperties: () => void;
  identify: (identity: string) => void;
  resetIdentity: () => void;
  removeEventProperty: (property: string) => void;
  setEventProperties: (properties: Record<string, unknown>) => void;
  track: (event: string, properties?: Record<string, unknown>) => void;
  unsetEventProperty: (property: string) => void;
};

type HeapInstance = HeapQueue | HeapLoaded;

type ExtendedWindow = Window & {
  heap: HeapInstance;
};
